import { useMemo } from "react";
import {
  backtestSummaryFromNav,
  resolveNavPeriodBounds,
  getSignalSetFrequencyMetrics,
} from "./backtestMetrics";

const EMPTY_METRICS_ROW = {
  backtestPeriod: "—",
  days: "—",
  totalReturn: "0%",
  annualized: "0% annualized",
  sharpeRatio: "0",
  maxDrawdown: "0%",
  drawdownDuration: "0 days duration",
};

function formatBacktestDaysLabel(startDate, endDate) {
  if (!startDate || !endDate) return "—";
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const years = (diffDays / 365.25).toFixed(2);
  return `${diffDays} days (${years} years)`;
}

function finiteNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function formatPct(value, suffix = "%") {
  const n = finiteNumber(value);
  return n === null ? "—" : `${n.toFixed(2)}${suffix}`;
}

function formatFixed(value, fallback = "—") {
  const n = finiteNumber(value);
  return n === null ? fallback : n.toFixed(2);
}

/**
 * Custom hook to calculate strategy performance metrics
 */
export const useStrategyMetrics = ({
  strategyVal,
  historicalSignalSet,
  period,
  customFromDate,
  customToDate,
  appliedCustomFromDate,
  appliedCustomToDate,
}) => {
  // Calculate backtest metrics from NAV series using backtestSummaryFromNav
  // ALWAYS use original, non-rebased strategyNavSeries for metrics (independent of period filters)
  const calculatedMetrics = useMemo(() => {
    if (!strategyVal || !historicalSignalSet?.navs?.strategyNavSeries) {
      return null;
    }

    try {
      const strategyNavSeriesData = historicalSignalSet.navs.strategyNavSeries;
      if (!strategyNavSeriesData) return null;

      const { startDate, endDate } = resolveNavPeriodBounds(
        strategyNavSeriesData,
        period,
        customFromDate,
        customToDate,
        appliedCustomFromDate,
        appliedCustomToDate
      );

      const navSeries = new Map(
        Object.entries(strategyNavSeriesData).map(([date, nav]) => [date, nav])
      );

      if (!startDate || !endDate || !navSeries || navSeries.size === 0)
        return null;

      // Optional configuration
      const options = {
        tradingDays: 252,
        riskFreeRateAnnual: 0.02,
        ddof: 1,
        sortinoThreshold: 0,
        ignoreNonTradingDays: true,
        mode: "calendar",
      };

      return backtestSummaryFromNav(navSeries, startDate, endDate, options);
    } catch (error) {
      console.error("Error calculating backtest metrics:", error);
      return null;
    }
  }, [
    strategyVal,
    historicalSignalSet,
    period,
    customFromDate,
    customToDate,
    appliedCustomFromDate,
    appliedCustomToDate,
  ]);

  // Calculate metric data for display
  const metricData = useMemo(() => {
    const bounds = resolveNavPeriodBounds(
      historicalSignalSet?.navs?.strategyNavSeries,
      period,
      customFromDate,
      customToDate,
      appliedCustomFromDate,
      appliedCustomToDate
    );

    const freqMetrics =
      strategyVal && bounds.startDate && bounds.endDate
        ? getSignalSetFrequencyMetrics(
            historicalSignalSet?.signalSets,
            bounds.startDate,
            bounds.endDate
          )
        : null;

    const signalSetFrequencyDays =
      freqMetrics?.averageRounded != null
        ? String(freqMetrics.averageRounded)
        : "—";
    const signalSetTradingDays = freqMetrics?.totalSignalsInPeriod ?? 0;
    const signalCalendarYmds = freqMetrics?.signalCalendarYmds ?? [];
    const signalSetsByCalendarYmd = freqMetrics?.signalsByYmd ?? {};
    const signalCalendarPeriodStartYmd = bounds.startDate ?? "";
    const signalCalendarPeriodEndYmd = bounds.endDate ?? "";
    const frequencyFields = {
      signalSetFrequencyDays,
      signalSetTradingDays,
      signalCalendarYmds,
      signalSetsByCalendarYmd,
      signalCalendarPeriodStartYmd,
      signalCalendarPeriodEndYmd,
    };

    if (!strategyVal) {
      return {
        ...EMPTY_METRICS_ROW,
        signalSetFrequencyDays: "—",
        signalSetTradingDays: 0,
        signalCalendarYmds: [],
        signalSetsByCalendarYmd: {},
        signalCalendarPeriodStartYmd: "",
        signalCalendarPeriodEndYmd: "",
      };
    }

    // Use calculatedMetrics if available (calculated from selected benchmark/period)
    if (calculatedMetrics) {
      const backtest = calculatedMetrics.backtest || {};
      const startDate = backtest.effectiveStartDate || backtest.startDate || "";
      const endDate = backtest.effectiveEndDate || backtest.endDate || "";
      const backtestPeriod =
        startDate && endDate ? `${startDate} to ${endDate}` : "—";
      const days = formatBacktestDaysLabel(startDate, endDate);

      const worstDrawdown = calculatedMetrics.worstDrawdown || {};
      const totalReturn = formatPct(calculatedMetrics.totalTimeWeightedReturnPct);
      const annualized = formatPct(
        calculatedMetrics.annualizedReturnPct,
        "% annualized"
      );
      const sharpeRatio = formatFixed(calculatedMetrics.sharpe, "—");
      const maxDrawdown = formatPct(worstDrawdown.drawdownPct);
      const drawdownDuration =
        finiteNumber(worstDrawdown.daysFromStartToEnd) !== null
          ? `${worstDrawdown.daysFromStartToEnd} days duration`
          : "0 days duration";

      return {
        backtestPeriod,
        days,
        totalReturn,
        annualized,
        sharpeRatio,
        maxDrawdown,
        drawdownDuration,
        ...frequencyFields,
      };
    }

    // Fallback to backend data if calculatedMetrics is not available
    if (!historicalSignalSet?.backtestSummary) {
      return { ...EMPTY_METRICS_ROW, ...frequencyFields };
    }

    const summary = historicalSignalSet.backtestSummary;
    const backtest = summary.backtest || {};

    // Format dates
    const startDate = backtest.startDate || backtest.effectiveStartDate || "";
    const endDate = backtest.endDate || backtest.effectiveEndDate || "";
    const backtestPeriod =
      startDate && endDate ? `${startDate} to ${endDate}` : "—";
    const days = formatBacktestDaysLabel(startDate, endDate);

    const worstDrawdown = summary.worstDrawdown || {};
    const totalReturn = formatPct(summary.totalTimeWeightedReturnPct);
    const annualized = formatPct(summary.annualizedReturnPct, "% annualized");
    const sharpeRatio = formatFixed(summary.sharpe, "—");
    const maxDrawdown = formatPct(worstDrawdown.drawdownPct);
    const drawdownDuration =
      finiteNumber(worstDrawdown.daysFromStartToEnd) !== null
        ? `${worstDrawdown.daysFromStartToEnd} days duration`
        : "0 days duration";

    return {
      backtestPeriod,
      days,
      totalReturn,
      annualized,
      sharpeRatio,
      maxDrawdown,
      drawdownDuration,
      ...frequencyFields,
    };
  }, [
    strategyVal,
    calculatedMetrics,
    historicalSignalSet,
    period,
    customFromDate,
    customToDate,
    appliedCustomFromDate,
    appliedCustomToDate,
  ]);

  return { calculatedMetrics, metricData };
};

